// liveClient — THE ONE LIVE CLIENT of a browser tab (ADR-074 §1): the only piece
// of the frontend that owns a timer for freshness. Plain TypeScript, no React —
// the loop is a `setTimeout` chain a test drives with fake timers.
//
// What it does, whole:
//   • THE GLANCE — one short poll of the project's glance route. The rhythm is
//     the SERVER's, re-read from every answer (§5): obeyed with jitter, backed
//     off on failure (the cap a multiple of the server's rhythm), `retry-after`
//     honoured on a 429. A hidden tab pauses; it glances at once on return, when
//     the browser comes back online, and right after the person's OWN write —
//     merged, so a burst of writes is one glance.
//   • WHAT EACH LIVE QUESTION DEPENDS ON is learnt from its own answers
//     (`learn`, fed by `liveGet`) — nobody hand-keeps the map (§3).
//   • LOOK AGAIN — the glance's token differs from the answer's → THAT exact
//     question is re-asked (never a key prefix), joining a read already in
//     flight instead of restarting it: the person's own save is read once.
//     Only a question on screen (an active query) is re-asked. A question whose
//     re-read keeps failing is re-asked less and less often (a broken route is
//     not hammered by every open tab); a Retry, a return to the tab or the
//     browser coming back online re-asks it at once.
//   • THE SAFETY RE-READ (§6 — the periodic resync) — a question whose answer
//     is older than the server's resync period is re-asked whatever the tokens
//     say.
//   • THE PERSON'S RIGHTS — no read carries that token, so it is the one kind
//     compared between TWO glances: when it moves, "who am I" and every live
//     question on screen are re-read at once (the first glance only sets the
//     baseline; the 60 s `['me']` check stays the net — ADR-003).
//   • CANNOT REFRESH (§9) — a failed glance is normal and silent. Past the
//     server's threshold of silence the report says `cut-off`; after several
//     thresholds of failing WHILE VISIBLE, `cut-off-long` (hours asleep are not
//     hours of failure). A look-again that meets silence counts toward the same
//     sign — one the browser PAUSED because it believes it is offline included.
//     The first good contact clears it and re-reads everything live. A refusal
//     (403 · 404 · 410) is an ANSWER, never silence; so is a fault of our own
//     code. The connection is the TAB's: a project switch keeps what is known of
//     it (the sign, the backoff, a 429's hold, the gap between two glances).
//   • BUSY IS AN ANSWER (the fuse — ADR-079 §5), told apart by the body's CODE,
//     never the status (a proxy's HTML 503 stays silence): a busy look-again
//     keeps its last answer, raises no sign, and is re-asked at the next glance
//     once the server's hint has passed — never before it; a busy glance
//     honours the hint the same way and neither raises the sign nor backs off.
//
// A question is EITHER counter-driven (it names tokens) OR rhythm-driven (data
// no counter sees — §7: re-asked on every glance). The second is a seam with a
// test and no consumer yet.
//
// Level-triggered throughout (§7): every decision compares CURRENT state, so a
// missed glance is a delayed refresh, never a wrong screen.

import { hashKey, isCancelledError, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { BUSY_FALLBACK_S, codeOf, retryAfterOf, statusOf } from '../refusal.js';
import type { ChangeTokens, GlanceResponse } from '@ems-app/schemas/live/responses';

/** What a live question's freshness hangs on. */
export type LiveDependency = { tokens: ChangeTokens } | { rhythm: true };

/** The glance's outcome, as the transport hands it over — it never rejects. A
 *  failure carries the body's code when the server answered with one (`BUSY`). */
export type GlanceResult = { ok: true; data: unknown } | { ok: false; status: number | null; retryAfterS: number | null; code: string | null };

/** `off` outside a project · `ok` · `cut-off` past the server's threshold of
 *  silence · `cut-off-long` after several thresholds of failing while visible. */
export type LiveStatus = 'off' | 'ok' | 'cut-off' | 'cut-off-long';

/** Only what the UI renders. */
export interface LiveReport {
  status: LiveStatus;
  /** The SERVER's clock at the last good contact (ms) — a wrong local clock cannot lie; `null` before any. */
  lastContactAt: number | null;
  /** The running server's version, as last heard. */
  version: string | null;
  /** Was that last contact TODAY, by this browser's calendar? (the sign names a
   *  bare time then, the date too otherwise) — re-judged at every check, so it
   *  turns at the first check after midnight. */
  sinceToday: boolean;
  /** A check the PERSON asked for (Retry) is waiting or out — said only while the
   *  sign shows. The tab's own checks never move it. */
  checking: boolean;
}

/** Which of the server's two thresholds of silence this screen obeys. */
export type ScreenClass = keyof GlanceResponse['stale_after_s'];

export interface LiveClientDeps {
  queryClient: QueryClient;
  glance(projectId: string, signal: AbortSignal): Promise<GlanceResult>;
  /** "Who am I" — re-read when the project's rights move. */
  meQueryKey: QueryKey;
  report(next: LiveReport): void;
  now?(): number;
  random?(): number;
}

export interface LiveClient {
  /** Glance for this project from now on (a no-op when already on it). */
  start(projectId: string, opts?: { screenClass?: ScreenClass }): void;
  stop(): void;
  /** A live read answered: what its question depends on (`null` — the answer named nothing: not live). */
  learn(projectId: string, queryKey: QueryKey, dependency: LiveDependency | null): void;
  /** Is the client itself re-asking this question right now? (a background read —
   *  it never navigates, and it shows no "refreshing" sign) */
  isLookingAgain(queryKey: QueryKey): boolean;
  /** The person's own write just landed on this project: ask what changed at once. */
  noteOwnWrite(projectId: string): void;
  /** Check now — the cannot-refresh sign's Retry. */
  checkNow(): void;
}

/** Until the first answer names the server's numbers — never used after it. */
const FIRST_CONTACT = { rhythmS: 10, resyncS: 300, staleAfterS: { page: 120, always_on: 30 } };
/** A floor under a rhythm the server would never send — the loop must not spin. */
const MIN_RHYTHM_S = 1;
const JITTER = 0.1;
/** The backoff's cap, in rhythms: a failing tab still checks about once a minute at the default rhythm. */
const BACKOFF_CAP_RHYTHMS = 6;
/** A question whose re-read keeps failing is re-asked every 1 · 2 · 4 · 8 glances, then every 8. */
const FAILING_MAX_GAP = 8;
/** One burst of the person's own writes (an upload is three calls) is one glance. */
const OWN_WRITE_MERGE_MS = 400;
/** Never two glances inside a second, whatever asks — the glance has a rate-limit bucket of its own. */
const MIN_GAP_MS = 1_000;
/** The sign turns stronger after this many thresholds of failing while visible. */
const LONG_CUT_OFF = 5;
/** One bad check never shows the sign by itself: a tab back from hours hidden (a
 *  laptop waking before its network) has a long silence BY DESIGN — the sign
 *  escalates with duration, never with a blip (§9). */
const BLIPS_FORGIVEN = 1;

interface Glance {
  tokens: ChangeTokens;
  rhythmS: number;
  resyncS: number | null;
  staleAfterS: Record<ScreenClass, number | null>;
  serverTime: number | null;
  version: string | null;
}

/** What belongs to ONE project's loop — rebuilt at every `start`. */
interface Run {
  projectId: string;
  screenClass: ScreenClass;
  timer: ReturnType<typeof setTimeout> | null;
  dueAt: number;
  /** The glance in flight. */
  flight: AbortController | null;
  /** Something asked for a glance while one was in flight — it may have read the counters too early. */
  again: boolean;
  /** `undefined` until a glance sets the baseline. */
  rights: string | undefined;
}

/** A question whose last re-read did not answer. */
interface Failing {
  failures: number;
  /** Glances still to skip before it is re-asked. */
  wait: number;
  /** Did it meet SILENCE (unreachable · 5xx · 429 · paused offline)? Only silence counts toward the sign. */
  silence: boolean;
  /** A BUSY answer's hint: not re-asked before this instant, whatever asks. */
  notBefore: number;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const seconds = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

/** Read a glance TOLERANTLY — a tab outlives many servers: unknown fields and
 *  unknown token names are ignored, a missing number keeps the last one heard.
 *  What is not a glance at all (a proxy's HTML page answering 200) is a FAILED
 *  glance — that screen is cut off. */
function readGlance(data: unknown): Glance | null {
  if (!isRecord(data) || !isRecord(data.tokens)) return null;
  const rhythmS = seconds(data.rhythm_s);
  if (rhythmS === null) return null;
  const tokens: ChangeTokens = {};
  for (const [name, token] of Object.entries(data.tokens)) if (typeof token === 'string' && token !== '') tokens[name] = token;
  const serverTime = typeof data.server_time === 'string' ? Date.parse(data.server_time) : NaN;
  const stale = isRecord(data.stale_after_s) ? data.stale_after_s : {};
  return {
    tokens,
    rhythmS: Math.max(rhythmS, MIN_RHYTHM_S),
    resyncS: seconds(data.resync_s),
    staleAfterS: { page: seconds(stale.page), always_on: seconds(stale.always_on) },
    serverTime: Number.isFinite(serverTime) ? serverTime : null,
    version: typeof data.version === 'string' && data.version !== '' ? data.version : null,
  };
}

/** The server answered — but could not serve: silence. Any other answer is an answer. */
const isSilentStatus = (status: number): boolean => status >= 500 || status === 429 || status === 408;
/** Refused for good: the right was lost, the thing is gone. */
const isRefusal = (status: number): boolean => status === 403 || status === 404 || status === 410;
const TRANSPORT_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);
/** A failed read with NO answer is silence only when the TRANSPORT failed — a
 *  fault of our own code (a transform that throws) is not "no connection". */
const isTransportFailure = (error: unknown): boolean => !(error instanceof Error) || TRANSPORT_CODES.has(String((error as { code?: unknown }).code));

const isHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const sameLocalDay = (a: number, b: number): boolean => new Date(a).toDateString() === new Date(b).toDateString();

export function createLiveClient(deps: LiveClientDeps): LiveClient {
  const now = deps.now ?? (() => Date.now());
  const random = deps.random ?? (() => Math.random());
  const cache = deps.queryClient.getQueryCache();

  // queryHash → what the question depends on. Keyed by TanStack's own hash of
  // the key (no query sets a hash function of its own), so "the exact question"
  // is one string. An entry dies with its query — swept at every glance.
  const registry = new Map<string, { projectId: string; dependency: LiveDependency }>();
  const failing = new Map<string, Failing>();
  /** Look-agains in flight, by question. */
  const lookingAgain = new Map<string, number>();

  // THE TAB'S CONNECTION — one network path whatever the project, so nothing
  // here resets on a project switch: the sign, the backoff, a 429's hold, the
  // gap between two glances, the server's numbers.
  const tab = {
    lastStartedAt: Number.NEGATIVE_INFINITY,
    /** A 429's `retry-after`: nothing glances before it. */
    notBefore: 0,
    /** Consecutive failed glances — the backoff. */
    failures: 0,
    /** Consecutive checks that were not good — the sign. */
    badChecks: 0,
    /** Since when the tab has been failing WHILE VISIBLE (hidden time is not failing time). */
    badSince: null as number | null,
    lastGood: { local: now(), server: null as number | null },
    status: 'ok' as Exclude<LiveStatus, 'off'>,
    version: null as string | null,
    /** A check the person asked for is waiting or out. */
    asked: false,
    rhythmMs: FIRST_CONTACT.rhythmS * 1000,
    resyncMs: FIRST_CONTACT.resyncS * 1000,
    staleAfterMs: { page: FIRST_CONTACT.staleAfterS.page * 1000, always_on: FIRST_CONTACT.staleAfterS.always_on * 1000 } as Record<ScreenClass, number>,
  };

  let run: Run | null = null;
  let last: LiveReport = { status: 'off', lastContactAt: null, sinceToday: true, version: null, checking: false };

  function report(next: LiveReport): void {
    if (next.status === last.status && next.lastContactAt === last.lastContactAt && next.sinceToday === last.sinceToday && next.version === last.version && next.checking === last.checking) return;
    last = next;
    deps.report(next);
  }

  /** The last contact and the asked-for check are said only while the sign
   *  shows — a healthy tab does not move the store at every glance. */
  function publish(): void {
    const showing = tab.status !== 'ok';
    const at = showing ? tab.lastGood.server : null;
    report({ status: tab.status, lastContactAt: at, sinceToday: at === null || sameLocalDay(at, now()), version: tab.version, checking: showing && tab.asked });
  }

  const jittered = (ms: number): number => ms * (1 - JITTER + 2 * JITTER * random());

  function disarm(r: Run): void {
    if (r.timer !== null) clearTimeout(r.timer);
    r.timer = null;
  }

  /** Arm the next glance — never while hidden (the return glances at once),
   *  never inside the minimum gap, never before a 429's `retry-after`. */
  function arm(r: Run, delayMs: number): void {
    disarm(r);
    if (isHidden()) return;
    const at = Math.max(now() + delayMs, tab.lastStartedAt + MIN_GAP_MS, tab.notBefore);
    r.dueAt = at;
    r.timer = setTimeout(() => void glanceNow(r), at - now());
  }

  /** Ask for a glance within `delayMs` — keeps one that is already due sooner. */
  function soon(delayMs: number): void {
    const r = run;
    if (!r) return;
    if (r.flight) {
      r.again = true;
      return;
    }
    if (r.timer !== null && r.dueAt <= now() + delayMs) return;
    arm(r, delayMs);
  }

  function markFailing(hash: string, silence: boolean): void {
    const failures = (failing.get(hash)?.failures ?? 0) + 1;
    failing.set(hash, { failures, silence, wait: Math.min(2 ** (failures - 1), FAILING_MAX_GAP) - 1, notBefore: 0 });
  }
  /** A BUSY answer (ADR-079 §5): not silence, no backoff — re-asked at the first glance after the hint. */
  function markBusy(hash: string, error: unknown): void {
    failing.set(hash, { failures: 0, silence: false, wait: 0, notBefore: now() + Math.min((retryAfterOf(error) ?? BUSY_FALLBACK_S) * 1000, tab.resyncMs) });
  }
  /** The person is back, or asked: every failing question is re-asked at the next glance. */
  function askFailingAtOnce(): void {
    for (const f of failing.values()) f.wait = 0;
  }

  function lookAgain(hash: string): void {
    lookingAgain.set(hash, (lookingAgain.get(hash) ?? 0) + 1);
    // The outcome is read from the QUERY once the re-read settles (never from a
    // rejection: a read TanStack paused or cancelled would leave one unhandled).
    void deps.queryClient
      .refetchQueries({ predicate: (q) => q.queryHash === hash, type: 'active' }, { cancelRefetch: false })
      .then(() => {
        const state = cache.get(hash)?.state;
        if (!state) return;
        if (state.fetchStatus === 'paused') {
          // The browser believes it is offline: nothing was read — silence, not a
          // success. It clears by itself: the resumed read answers, and `learn`
          // forgives the question.
          markFailing(hash, true);
        } else if (state.status === 'error' && !isCancelledError(state.error)) {
          const status = statusOf(state.error);
          if (status !== null && isRefusal(status)) {
            // An answer, not silence — and no longer a live question until it answers again.
            failing.delete(hash);
            registry.delete(hash);
          } else if (codeOf(state.error) === 'BUSY') markBusy(hash, state.error);
          else markFailing(hash, status === null ? isTransportFailure(state.error) : isSilentStatus(status));
        } else failing.delete(hash);
      })
      .finally(() => {
        const n = (lookingAgain.get(hash) ?? 1) - 1;
        if (n > 0) lookingAgain.set(hash, n);
        else lookingAgain.delete(hash);
      });
  }

  function sweep(): void {
    for (const hash of registry.keys()) if (!cache.get(hash)) registry.delete(hash);
    for (const hash of failing.keys()) if (!registry.has(hash)) failing.delete(hash);
  }

  /** Is a question ON SCREEN still meeting silence? (one the person left no longer speaks for the screen) */
  const lookAgainMeetsSilence = (): boolean => [...failing].some(([hash, f]) => f.silence && cache.get(hash)?.isActive());

  const differs = (answer: ChangeTokens, glance: ChangeTokens): boolean =>
    Object.entries(answer).some(([name, token]) => glance[name] !== undefined && glance[name] !== token);

  function onAnswer(r: Run, g: Glance): void {
    tab.rhythmMs = g.rhythmS * 1000;
    if (g.resyncS !== null) tab.resyncMs = g.resyncS * 1000;
    for (const screenClass of ['page', 'always_on'] as const) {
      const s = g.staleAfterS[screenClass];
      if (s !== null) tab.staleAfterMs[screenClass] = s * 1000;
    }
    if (g.version !== null) tab.version = g.version;

    const rights = g.tokens.rights;
    const rightsMoved = r.rights !== undefined && rights !== undefined && rights !== r.rights;
    if (rights !== undefined) r.rights = rights;
    if (rightsMoved) void deps.queryClient.invalidateQueries({ queryKey: deps.meQueryKey });

    // Everything on screen is re-read when the rights moved, and at the first
    // good contact after the sign showed (while a look-again still meets
    // silence, only IT is re-asked — the contact is not good yet).
    sweep();
    const everything = rightsMoved || (tab.status !== 'ok' && !lookAgainMeetsSilence());
    for (const [hash, entry] of registry) {
      const query = cache.get(hash);
      if (!query || entry.projectId !== r.projectId || !query.isActive()) continue;
      const failed = failing.get(hash);
      // A BUSY answer's hint holds the question, whatever else asks (§5).
      if (failed && now() < failed.notBefore) continue;
      if (failed && !everything) {
        // Its tokens and its age stay "due" for as long as it fails — the backoff decides.
        if (failed.wait > 0) failed.wait -= 1;
        else lookAgain(hash);
        continue;
      }
      const age = now() - query.state.dataUpdatedAt;
      const due =
        everything ||
        // Rhythm-driven: every glance — but never an answer that just arrived
        // (the page's own load, a glance made for the person's own write).
        ('rhythm' in entry.dependency ? age >= tab.rhythmMs / 2 : differs(entry.dependency.tokens, g.tokens)) ||
        age >= tab.resyncMs;
      if (due) lookAgain(hash);
    }
  }

  function settle(r: Run, good: boolean, serverTime: number | null): void {
    if (good) {
      tab.lastGood = { local: now(), server: serverTime };
      tab.badChecks = 0;
      tab.badSince = null;
      tab.status = 'ok';
    } else {
      tab.badChecks += 1;
      tab.badSince ??= now();
      const staleAfterMs = tab.staleAfterMs[r.screenClass];
      if (tab.badChecks > BLIPS_FORGIVEN && now() - tab.lastGood.local >= staleAfterMs) {
        // The stronger tone is earned by failing WHILE VISIBLE — never by hours asleep.
        const long = tab.status === 'cut-off-long' || now() - tab.badSince >= LONG_CUT_OFF * staleAfterMs;
        tab.status = long ? 'cut-off-long' : 'cut-off';
      }
    }
    publish();
  }

  async function glanceNow(r: Run): Promise<void> {
    r.timer = null;
    if (run !== r || r.flight || isHidden()) return;
    const flight = new AbortController();
    r.flight = flight;
    r.again = false;
    tab.lastStartedAt = now();
    let result: GlanceResult;
    try {
      result = await deps.glance(r.projectId, flight.signal);
    } catch {
      result = { ok: false, status: null, retryAfterS: null, code: null };
    }
    if (run !== r || flight.signal.aborted) return;
    r.flight = null;
    tab.asked = false;

    const glance = result.ok ? readGlance(result.data) : null;
    let delay: number;
    if (glance) {
      tab.failures = 0;
      onAnswer(r, glance);
      settle(r, !lookAgainMeetsSilence(), glance.serverTime);
      delay = jittered(tab.rhythmMs);
    } else {
      const status = result.ok ? null : result.status;
      // BUSY is an ANSWER (ADR-079 §5): the server is full, not gone — the sign
      // stays as it is, nothing backs off; the hint paces the next glance
      // (`arm` honours `notBefore`).
      const busy = !result.ok && result.code === 'BUSY';
      if (!busy) tab.failures += 1;
      if (!result.ok && result.retryAfterS !== null) tab.notBefore = now() + Math.min(result.retryAfterS * 1000, tab.resyncMs);
      // A refused glance is apiClient's business (it takes the person out —
      // a revoked session, a suspended project, a removed member): not silence.
      if (busy) publish();
      else if (status === null || isSilentStatus(status)) settle(r, false, null);
      else publish();
      if (busy) delay = jittered(tab.rhythmMs);
      else {
        delay = jittered(Math.min(tab.rhythmMs * 2 ** (tab.failures - 1), tab.rhythmMs * BACKOFF_CAP_RHYTHMS));
        // The sign speaks at a CHECK — so a check lands on the moment it falls due,
        // never a whole backoff later.
        const staleAfterMs = tab.staleAfterMs[r.screenClass];
        const dueAt = tab.status === 'ok' ? tab.lastGood.local + staleAfterMs : tab.status === 'cut-off' && tab.badSince !== null ? tab.badSince + LONG_CUT_OFF * staleAfterMs : null;
        if (dueAt !== null && dueAt > now()) delay = Math.min(delay, dueAt - now());
      }
    }
    arm(r, r.again ? Math.min(delay, OWN_WRITE_MERGE_MS) : delay);
  }

  function onVisibility(): void {
    const r = run;
    if (!r) return;
    if (isHidden()) {
      disarm(r);
      return;
    }
    // Back: the time away was not failing time, and whatever failed is asked again now.
    if (tab.badSince !== null) tab.badSince = now();
    askFailingAtOnce();
    soon(0);
  }
  function onOnline(): void {
    askFailingAtOnce();
    soon(0);
  }

  function stop(): void {
    const r = run;
    if (!r) return;
    run = null;
    disarm(r);
    r.flight?.abort();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
    sweep();
    report({ status: 'off', lastContactAt: null, sinceToday: true, version: tab.version, checking: false });
  }

  return {
    start(projectId, opts) {
      if (run?.projectId === projectId) return;
      stop();
      // No trouble in progress → the silence is counted from this start (a tab may
      // have sat outside any project for hours). Trouble in progress is the tab's.
      if (tab.status === 'ok' && tab.badChecks === 0) tab.lastGood.local = now();
      const r: Run = { projectId, screenClass: opts?.screenClass ?? 'page', timer: null, dueAt: 0, flight: null, again: false, rights: undefined };
      run = r;
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
      publish();
      arm(r, 0);
    },
    stop,
    learn(projectId, queryKey, dependency) {
      const hash = hashKey(queryKey);
      failing.delete(hash);
      const live = dependency !== null && ('rhythm' in dependency || Object.keys(dependency.tokens).length > 0);
      if (live) registry.set(hash, { projectId, dependency });
      else registry.delete(hash);
    },
    isLookingAgain(queryKey) {
      const hash = hashKey(queryKey);
      // …and really out: a re-read the browser paused is not "the client re-asking".
      return lookingAgain.has(hash) && cache.get(hash)?.state.fetchStatus === 'fetching';
    },
    noteOwnWrite(projectId) {
      if (run?.projectId === projectId) soon(OWN_WRITE_MERGE_MS);
    },
    checkNow() {
      if (!run) return;
      // Said at once — a check held back (a 429's hold, the gap) still shows it was asked.
      tab.asked = true;
      publish();
      askFailingAtOnce();
      soon(0);
    },
  };
}
